import { useMemo } from "react";
import { Trash2, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/ui/components/ui/dialog";
import { Button } from "@/ui/components/ui/button";
import { ScrollArea } from "@/ui/components/ui/scroll-area";
import { useAppDialog } from "@/state/legacy_module_api";
import { useActions, useAppState } from "@/state/legacy_module_api";
import { type Member } from "@/data/schemas";
import { cn } from "@/utils/module_api";

interface GroupMembersPopupProps {
  open: boolean;
  onClose: () => void;
  groupId?: string;
}

const GroupMembersPopup = ({ open, onClose, groupId }: GroupMembersPopupProps) => {
  const { threadMap } = useAppState().group;
  const { myUserId } = useAppState();
  const contactsById = useAppState().user?.contactsById || {};
  const { deleteMember } = useActions().group;
  const { openDialog } = useAppDialog();

  const group = groupId ? threadMap[groupId] : undefined;
  const isOwner = group?.ownerId === myUserId;
  const members = useMemo(() => group?.members || [], [group]);
  const resolveMemberName = (member: Member) =>
    contactsById[member.userId]?.fullHandle ||
    contactsById[member.userId]?.username ||
    member.userId;

  const handleRemove = (member: Member) => {
    if (!group) return;
    if (!isOwner || member.role === "owner") {
      openDialog({
        type: "alert",
        message:
          "Only the group owner can remove members, and the owner cannot be removed.",
      });
      return;
    }

    const displayName = resolveMemberName(member);
    openDialog({
      type: "confirm",
      title: "Remove Member",
      message: `Remove ${displayName} from ${group.name}?`,
      onConfirm: async () => {
        try {
          await deleteMember({ groupId: group.id, userId: member.userId });
        } catch (error) {
          console.error("Failed to remove member", error);
          openDialog({
            type: "alert",
            message: "Failed to remove member. Please try again.",
          });
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-muted-foreground" />
            {group ? `${group.name} Members` : "Group Members"}
          </DialogTitle>
          <DialogDescription>
            {group
              ? `${members.length} member${members.length === 1 ? "" : "s"}`
              : "Select a group to view members"}
          </DialogDescription>
        </DialogHeader>

        {!group ? (
          <p className="text-sm text-muted-foreground">Group not found.</p>
        ) : members.length === 0 ? (
          <p className="text-sm text-muted-foreground">No members yet.</p>
        ) : (
          <ScrollArea className="max-h-[60vh] pr-2">
            <div className="space-y-2">
              {members.map((member) => {
                const displayName = resolveMemberName(member);
                return (
                <div
                  key={member.userId}
                  className={cn(
                    "flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2",
                    member.role === "owner" && "border-primary/40"
                  )}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted font-semibold">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {member.role}
                    </p>
                  </div>
                  {isOwner && member.role !== "owner" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => handleRemove(member)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GroupMembersPopup;
